import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Create the Favorites Context
const FavoritesContext = createContext(null);

// Custom hook to use the favorites context
export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

// Favorites Provider Component
export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState([]);
  const [isHeartAnimating, setIsHeartAnimating] = useState(null);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('StuffLab-favorites');
    if (savedFavorites) {
      try {
        const parsedFavorites = JSON.parse(savedFavorites);
        setFavorites(parsedFavorites);
      } catch (error) {
        console.error('Error loading favorites from localStorage:', error);
        localStorage.removeItem('StuffLab-favorites');
      }
    }
  }, []);

  // Save favorites to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('StuffLab-favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Check if an item is favorited
  const isFavorited = useCallback((productId) => {
    return favorites.some((item) => item.id === productId);
  }, [favorites]);

  // Toggle favorite status
  const toggleFavorite = useCallback((product) => {
    setFavorites((prevFavorites) => {
      const existingIndex = prevFavorites.findIndex((item) => item.id === product.id);

      if (existingIndex > -1) {
        // Remove from favorites
        return prevFavorites.filter((item) => item.id !== product.id);
      }

      // Add to favorites with all required fields
      return [
        ...prevFavorites,
        {
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image || product.imageUrl || product.images?.[0],
          rating: product.rating,
          reviews: product.reviews,
          brand: product.brand || 'StuffLab',
          brandTag: product.brandTag,
          category: product.category,
          colors: product.colors,
          availableSizes: product.availableSizes,
          addedAt: new Date().toISOString(),
        },
      ];
    });

    // Trigger heart animation
    setIsHeartAnimating(product.id);
    setTimeout(() => setIsHeartAnimating(null), 600);
  }, []);

  // Add to favorites
  const addToFavorites = useCallback((product) => {
    if (!isFavorited(product.id)) {
      toggleFavorite(product);
    }
  }, [isFavorited, toggleFavorite]);

  // Remove from favorites
  const removeFromFavorites = useCallback((productId) => {
    setFavorites((prevFavorites) =>
      prevFavorites.filter((item) => item.id !== productId)
    );
  }, []);

  // Clear all favorites
  const clearFavorites = useCallback(() => {
    setFavorites([]);
  }, []);

  // Get total favorites count
  const favoritesCount = favorites.length;

  const value = {
    favorites,
    favoritesCount,
    isFavorited,
    toggleFavorite,
    addToFavorites,
    removeFromFavorites,
    clearFavorites,
    isHeartAnimating,
  };

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export default FavoritesContext;
